import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, Check, Pencil, Plus, Printer, Shuffle, Trash2, Users, X } from 'lucide-react'
import { useAuth } from '../../../hooks/useAuth'
import {
  affecterInscriptions,
  appliquerRepartition,
  creerSousGroupe,
  lireInscriptionsDeSession,
  lireSousGroupes,
  renommerSousGroupe,
  supprimerSousGroupe,
  type InscriptionGroupe,
  type SousGroupe,
} from '../../../../lib/groupes/groupes.supabase'
import {
  MODELES_DE_NOMS,
  ageAu,
  erreurNoms,
  nomsParDefaut,
  normaliserNom,
  rapprocherGroupes,
  repartirParAge,
  type GroupePlanifie,
} from '../../../../lib/groupes/repartition'
import type { Filtre, SessionGroupe } from '../../../../lib/groupes/sessions'
import { FiltresSessions } from './FiltresSessions'
import { useSessionsGroupes } from './useSessionsGroupes'

const message = (e: unknown, defaut: string) => (e instanceof Error && e.message ? e.message : defaut)

/**
 * Organiser les enfants d'une session en sous-groupes : les Petites et les
 * Grandes Hirondelles, les Jaunes, Bleus, Verts et Rouges des Colibris.
 *
 * Les listes de chaque groupe s'impriment ensuite depuis « Listings ».
 */
export function SousGroupesModule({
  onBack,
  onImprimer,
}: {
  onBack: () => void
  onImprimer: (filtre: Filtre) => void
}) {
  const { userProfile } = useAuth()
  const etat = useSessionsGroupes()
  const { filtrees } = etat

  const [sessionId, setSessionId] = useState('')
  const session = filtrees.find((s) => s.id === sessionId) ?? null

  const [groupes, setGroupes] = useState<SousGroupe[]>([])
  const [inscriptions, setInscriptions] = useState<InscriptionGroupe[]>([])
  const [chargement, setChargement] = useState(false)
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [nouveauNom, setNouveauNom] = useState('')
  const [renommage, setRenommage] = useState<{ id: string; nom: string } | null>(null)
  const [repartitionOuverte, setRepartitionOuverte] = useState(false)

  // Une session qui sort du filtre n'est plus affichée.
  useEffect(() => {
    if (sessionId && !filtrees.some((s) => s.id === sessionId)) setSessionId('')
  }, [filtrees, sessionId])

  const charger = useCallback(async (id: string) => {
    setChargement(true)
    setErreur(null)
    try {
      const [g, i] = await Promise.all([lireSousGroupes([id]), lireInscriptionsDeSession(id)])
      setGroupes(g)
      setInscriptions(i)
    } catch (e) {
      setErreur(message(e, 'Lecture de la session impossible.'))
    } finally {
      setChargement(false)
    }
  }, [])

  useEffect(() => {
    setGroupes([])
    setInscriptions([])
    setRenommage(null)
    if (sessionId) void charger(sessionId)
  }, [sessionId, charger])

  /** Exécute une écriture, puis relit la session : l'écran montre toujours la base. */
  async function ecrire(action: () => Promise<void>, echec: string) {
    if (!sessionId) return
    setOccupe(true)
    setErreur(null)
    try {
      await action()
    } catch (e) {
      setErreur(`${echec} ${message(e, '')}`.trim())
    } finally {
      await charger(sessionId)
      setOccupe(false)
    }
  }

  const deplacer = (inscriptionId: string, groupeId: string) => {
    const groupe = groupes.find((g) => g.id === groupeId) ?? null
    void ecrire(
      () => affecterInscriptions([inscriptionId], groupe ? { id: groupe.id, nom: groupe.nom } : null),
      'Le déplacement a échoué.',
    )
  }

  const creer = () => {
    const nom = normaliserNom(nouveauNom)
    const probleme = erreurNoms([...groupes.map((g) => g.nom), nom])
    if (probleme) {
      setErreur(probleme)
      return
    }
    void ecrire(async () => {
      await creerSousGroupe(sessionId, nom, userProfile?.id ?? null)
      setNouveauNom('')
    }, 'La création du groupe a échoué.')
  }

  const validerRenommage = () => {
    if (!renommage) return
    const autres = groupes.filter((g) => g.id !== renommage.id).map((g) => g.nom)
    const probleme = erreurNoms([...autres, renommage.nom])
    if (probleme) {
      setErreur(probleme)
      return
    }
    const { id, nom } = renommage
    setRenommage(null)
    void ecrire(() => renommerSousGroupe(id, nom), 'Le renommage a échoué.')
  }

  const supprimer = (g: SousGroupe) => {
    const nombre = inscriptions.filter((i) => i.sousGroupeId === g.id).length
    const suite = nombre ? ` Ses ${nombre} enfant(s) repasseront dans « Non assignés ».` : ''
    if (!window.confirm(`Supprimer le groupe « ${g.nom} » ?${suite}`)) return
    void ecrire(() => supprimerSousGroupe(g.id), 'La suppression a échoué.')
  }

  const appliquer = (plan: GroupePlanifie[]) => {
    setRepartitionOuverte(false)
    void ecrire(
      () => appliquerRepartition(sessionId, plan, groupes, userProfile?.id ?? null),
      'La répartition n’a été appliquée qu’en partie. Relancez-la : le résultat sera le même.',
    )
  }

  const colonnes = useMemo(() => {
    const connus = new Set(groupes.map((g) => g.id))
    return [
      {
        id: '',
        groupe: null as SousGroupe | null,
        enfants: inscriptions.filter((i) => !i.sousGroupeId || !connus.has(i.sousGroupeId)),
      },
      ...groupes.map((g) => ({ id: g.id, groupe: g as SousGroupe | null, enfants: inscriptions.filter((i) => i.sousGroupeId === g.id) })),
    ]
  }, [groupes, inscriptions])

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sous-groupes</h1>
          <p className="text-gray-600">Répartir les enfants d’une session en groupes</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <FiltresSessions etat={etat} />
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Session ({filtrees.length})</span>
          <select
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Choisir une session…</option>
            {filtrees.map((s) => (
              <option key={s.id} value={s.id}>
                {s.stage} · {s.centre} · {s.periode} {s.semaine} ({s.inscrits} inscrit(s))
              </option>
            ))}
          </select>
        </label>
        {etat.erreur && <p className="text-sm text-red-700">{etat.erreur}</p>}
      </div>

      {erreur && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{erreur}</span>
        </div>
      )}

      {session && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <input
              value={nouveauNom}
              onChange={(e) => setNouveauNom(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && nouveauNom.trim() && creer()}
              placeholder="Nom du nouveau groupe"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              disabled={occupe}
            />
            <button
              type="button"
              onClick={creer}
              disabled={occupe || !nouveauNom.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Ajouter le groupe
            </button>
            <button
              type="button"
              onClick={() => setRepartitionOuverte(true)}
              disabled={occupe || inscriptions.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Shuffle className="h-4 w-4" />
              Répartir par âge…
            </button>
            <button
              type="button"
              onClick={() => onImprimer({ centreId: session.centreId, periode: session.periode, semaine: session.semaine })}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 ml-auto"
            >
              <Printer className="h-4 w-4" />
              Imprimer les listes
            </button>
          </div>

          {chargement && inscriptions.length === 0 ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {colonnes.map(({ id, groupe, enfants }) => (
                <div key={id || 'non-assignes'} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className={`px-4 py-3 border-b border-gray-200 ${groupe ? 'bg-blue-50' : 'bg-gray-50'}`}>
                    {groupe && renommage?.id === groupe.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={renommage.nom}
                          onChange={(e) => setRenommage({ id: groupe.id, nom: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') validerRenommage()
                            if (e.key === 'Escape') setRenommage(null)
                          }}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <button type="button" onClick={validerRenommage} className="text-green-700" title="Valider">
                          <Check className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => setRenommage(null)} className="text-gray-500" title="Annuler">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {groupe ? groupe.nom : 'Non assignés'}
                          <span className="text-sm font-normal text-gray-500">({enfants.length})</span>
                        </h3>
                        {groupe && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={occupe}
                              onClick={() => setRenommage({ id: groupe.id, nom: groupe.nom })}
                              className="text-gray-500 hover:text-gray-800"
                              title="Renommer"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={occupe}
                              onClick={() => supprimer(groupe)}
                              className="text-red-600 hover:text-red-800"
                              title="Supprimer le groupe"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <ul className="p-3 space-y-2 max-h-[28rem] overflow-y-auto">
                    {enfants.length === 0 && <li className="text-center text-sm text-gray-400 py-4">Aucun enfant</li>}
                    {enfants.map((enfant) => (
                      <EnfantCarte
                        key={enfant.id}
                        enfant={enfant}
                        session={session}
                        groupes={groupes}
                        desactive={occupe}
                        onDeplacer={(groupeId) => deplacer(enfant.id, groupeId)}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {repartitionOuverte && (
            <ModaleRepartition
              session={session}
              inscriptions={inscriptions}
              groupes={groupes}
              onAnnuler={() => setRepartitionOuverte(false)}
              onAppliquer={appliquer}
            />
          )}
        </>
      )}
    </div>
  )
}

function EnfantCarte({
  enfant,
  session,
  groupes,
  desactive,
  onDeplacer,
}: {
  enfant: InscriptionGroupe
  session: SessionGroupe
  groupes: SousGroupe[]
  desactive: boolean
  onDeplacer: (groupeId: string) => void
}) {
  const age = ageAu(enfant.dateNaissance, session.debut)
  return (
    <li className="flex items-center justify-between gap-2 rounded border border-gray-200 px-3 py-2">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900 truncate">
          {enfant.prenom} {enfant.nom}
        </span>
        <span className="block text-xs text-gray-500">{age === null ? 'Date de naissance inconnue' : `${age} ans`}</span>
      </span>
      <select
        value={enfant.sousGroupeId && groupes.some((g) => g.id === enfant.sousGroupeId) ? enfant.sousGroupeId : ''}
        onChange={(e) => onDeplacer(e.target.value)}
        disabled={desactive}
        className="max-w-[45%] px-2 py-1 border border-gray-300 rounded text-xs"
        aria-label={`Groupe de ${enfant.prenom} ${enfant.nom}`}
      >
        <option value="">Non assigné</option>
        {groupes.map((g) => (
          <option key={g.id} value={g.id}>
            {g.nom}
          </option>
        ))}
      </select>
    </li>
  )
}

function ModaleRepartition({
  session,
  inscriptions,
  groupes,
  onAnnuler,
  onAppliquer,
}: {
  session: SessionGroupe
  inscriptions: InscriptionGroupe[]
  groupes: SousGroupe[]
  onAnnuler: () => void
  onAppliquer: (plan: GroupePlanifie[]) => void
}) {
  const [noms, setNoms] = useState<string[]>(() =>
    groupes.length >= 2 ? groupes.map((g) => g.nom) : nomsParDefaut(2),
  )

  const changerNombre = (nombre: number) =>
    setNoms((avant) =>
      Array.from({ length: nombre }, (_, i) => avant[i] ?? `Groupe ${i + 1}`),
    )

  const probleme = erreurNoms(noms)
  const plan = useMemo(
    () =>
      probleme
        ? []
        : repartirParAge(
            inscriptions.map((i) => ({ inscriptionId: i.id, nom: i.nom, prenom: i.prenom, dateNaissance: i.dateNaissance })),
            noms,
            session.debut,
          ),
    [probleme, inscriptions, noms, session.debut],
  )
  const supprimes = probleme ? [] : rapprocherGroupes(groupes.map((g) => ({ id: g.id, nom: g.nom })), noms).aSupprimer
  const sansDate = inscriptions.filter((i) => !i.dateNaissance).length

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Répartir par âge</h3>
          <p className="text-sm text-gray-600">
            {inscriptions.length} enfant(s), des plus jeunes aux plus âgés, en parts égales à un enfant près.
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Nombre de groupes{' '}
              <select
                value={noms.length}
                onChange={(e) => changerNombre(Number(e.target.value))}
                className="ml-2 px-2 py-1 border border-gray-300 rounded"
              >
                {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            {MODELES_DE_NOMS.map((m) => (
              <button
                key={m.libelle}
                type="button"
                onClick={() => setNoms([...m.noms])}
                className="px-2.5 py-1 rounded-full border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
              >
                {m.libelle}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {noms.map((nom, i) => (
              <label key={i} className="block text-sm">
                <span className="text-gray-600">
                  Groupe {i + 1}
                  {i === 0 && ' (les plus jeunes)'}
                  {i === noms.length - 1 && ' (les plus âgés)'}
                </span>
                <input
                  value={nom}
                  onChange={(e) => setNoms((avant) => avant.map((n, j) => (j === i ? e.target.value : n)))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </label>
            ))}
          </div>

          {probleme && <p className="text-sm text-red-700">{probleme}</p>}
          {sansDate > 0 && (
            <p className="text-sm text-amber-800">
              {sansDate} enfant(s) sans date de naissance : placé(s) dans le dernier groupe. À vérifier.
            </p>
          )}
          {supprimes.length > 0 && (
            <p className="text-sm text-amber-800">
              Remplace la répartition actuelle : {supprimes.map((g) => `« ${g.nom} »`).join(', ')} sera(ont) supprimé(s).
            </p>
          )}

          {!probleme && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plan.map((g) => (
                <div key={g.nom} className="rounded-lg border border-gray-200">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm">
                    <span className="font-semibold text-gray-900">{g.nom}</span>
                    <span className="text-gray-600">
                      {' '}
                      · {g.enfants.length} enfant(s)
                      {g.ageMin !== null && ` · ${g.ageMin === g.ageMax ? `${g.ageMin} ans` : `${g.ageMin} à ${g.ageMax} ans`}`}
                    </span>
                  </div>
                  <ul className="px-3 py-2 text-xs text-gray-700 max-h-40 overflow-y-auto columns-2">
                    {g.enfants.map((e) => (
                      <li key={e.inscriptionId}>
                        {e.prenom} {e.nom}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={onAnnuler} className="px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100">
            Annuler
          </button>
          <button
            type="button"
            disabled={probleme !== null}
            onClick={() => onAppliquer(plan)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            Appliquer la répartition
          </button>
        </div>
      </div>
    </div>
  )
}
